import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GaugeChart from 'react-gauge-chart';
import { FormattedMessage } from 'react-intl';
import {
  alarmLabelKey,
  CELL_RED,
  ClusterModule,
  getCellReadings,
  getClusterModules,
  getInverterBattery,
  hasBmsData,
  InverterBattery,
  PylontechModule
} from './sodioHomeBatteryUtils';

interface DetailedBatteryViewSodioHomeProps {
  clusterLabel: string;
  // Index of the cluster's inverter, and of the cluster within that inverter.
  invIdx: number;
  clIdx: number;
  // Position of the cluster across the whole installation — this is what the flat
  // BatteryRecord.Cluster{n} numbering follows.
  flatIdx: number;
  batteryRecord: any;
  inverterRecord: any;
}

const RED = CELL_RED;

const cardStyle = {
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  border: '2px solid #ccc',
  borderRadius: '12px',
  height: '100%'
};

const labelCellStyle = { fontWeight: 'bold' } as const;
const valueCellStyle = {
  whiteSpace: 'nowrap',
  paddingRight: '12px'
} as const;

// Matches the alarm-name fallback in Log.tsx: "BatteryDisconnected" -> "Battery Disconnected".
const splitCamelCase = (value: string): string =>
  value
    .replace(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/g, ' ')
    .trim();

const format = (
  value: number | null,
  unit: string,
  digits = 2
): string => (value == null ? '—' : `${value.toFixed(digits)} ${unit}`);

const CardTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h6"
    component="div"
    sx={{
      marginTop: '10px',
      marginBottom: '5px',
      borderBottom: '1px solid #ccc',
      fontWeight: 'bold',
      textAlign: 'center'
    }}
  >
    {children}
  </Typography>
);

const InfoTable = ({
  rows
}: {
  rows: { key: string; label: React.ReactNode; value: React.ReactNode }[];
}) => (
  <TableContainer component={Paper} sx={{ width: '100%' }}>
    <Table size="small">
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell component="th" scope="row" align="left" sx={labelCellStyle}>
              {row.label}
            </TableCell>
            <TableCell align="right" sx={valueCellStyle}>
              {row.value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Same guard as the other battery views: react-gauge-chart re-animates on every render,
// so only push a new value once the change is worth showing.
const SocGauge = ({ soc }: { soc: number }) => {
  const [stableSoc, setStableSoc] = useState(soc);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      setStableSoc(soc);
      isFirstRender.current = false;
    } else if (Math.abs(soc - stableSoc) > 0.1) {
      setStableSoc(soc);
    }
  }, [soc]);

  return (
    // react-gauge-chart sizes its height from its own width, and the label is a
    // normal-flow sibling below it. In this wide (md=6) card an unconstrained
    // width pushes the label out of the card, so cap the width and let the
    // wrapper grow with its content.
    <div
      style={{
        height: 'auto',
        width: '100%',
        maxWidth: '260px',
        textAlign: 'center'
      }}
    >
      <GaugeChart
        id="sodiohome-soc-gauge"
        nrOfLevels={30}
        colors={[RED, '#FFD700', '#00FF00']}
        arcWidth={0.3}
        percent={stableSoc / 100}
        textColor="transparent"
        animate={false}
        needleColor="#464A4F"
      />
      <div style={{ marginTop: '0px', fontWeight: 'bold', fontSize: '18px' }}>
        <div>SOC: {stableSoc.toFixed(1)}%</div>
      </div>
    </div>
  );
};

const ClusterSummary = ({ battery }: { battery: InverterBattery }) => {
  const rows = [
    {
      key: 'soc',
      label: <FormattedMessage id="soc" defaultMessage="SoC" />,
      value: format(battery.soc, '%', 1)
    },
    {
      key: 'soh',
      label: <FormattedMessage id="soh" defaultMessage="SoH" />,
      value: format(battery.soh, '%', 0)
    },
    {
      key: 'power',
      label: <FormattedMessage id="power" defaultMessage="Power" />,
      value: format(battery.power, 'W', 0)
    },
    {
      key: 'voltage',
      label: (
        <FormattedMessage id="batteryVoltage" defaultMessage="Battery Voltage" />
      ),
      value: format(battery.voltage, 'V', 2)
    },
    {
      key: 'current',
      label: <FormattedMessage id="current" defaultMessage="Current" />,
      value: format(battery.current, 'A', 2)
    },
    {
      key: 'temperature',
      label: (
        <FormattedMessage id="temperature" defaultMessage="Temperature" />
      ),
      value: format(battery.temperature, '°C', 1)
    },
    {
      key: 'systemState',
      label: <FormattedMessage id="systemState" defaultMessage="System State" />,
      value: battery.state ?? '—'
    }
  ];

  if (battery.bmsState != null) {
    rows.push({
      key: 'bmsState',
      label: <FormattedMessage id="bmsState" defaultMessage="BMS State" />,
      value: battery.bmsState
    });
  }

  return (
    <Grid container spacing={2} sx={{ marginTop: '10px' }}>
      <Grid item xs={12} md={6}>
        <Card sx={cardStyle}>
          <CardTitle>
            <FormattedMessage
              id="clusterSummary"
              defaultMessage="Cluster Summary"
            />
          </CardTitle>
          <InfoTable rows={rows} />
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ ...cardStyle, padding: '10px', minHeight: '240px' }}>
          <CardTitle>
            <FormattedMessage id="soc" defaultMessage="SoC" />
          </CardTitle>
          {battery.soc == null ? (
            <Typography variant="body2" sx={{ margin: 'auto' }}>
              <FormattedMessage
                id="batteryDataNotAvailable"
                defaultMessage="Battery data not available"
              />
            </Typography>
          ) : (
            <Box
              sx={{
                marginTop: '10px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <SocGauge soc={battery.soc} />
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};

const CellGrid = ({ module }: { module: PylontechModule }) => {
  const cells = getCellReadings(module);
  const perColumn = Math.ceil(cells.length / 2);

  // A module can report Valid:true with capacity and cycle data but no cell array.
  if (cells.length === 0) {
    return (
      <Typography variant="body2" sx={{ padding: '20px' }}>
        <FormattedMessage
          id="batteryDataNotAvailable"
          defaultMessage="Battery data not available"
        />
      </Typography>
    );
  }

  return (
    <Grid container spacing={1} sx={{ padding: '10px' }}>
      {[0, 1].map((column) => (
        <Grid item xs={6} key={column}>
          <Grid container direction="column" spacing={1}>
            {cells
              .slice(column * perColumn, (column + 1) * perColumn)
              .map((cell) => {
                const cellNumber = cell.index;
                return (
                  <Grid item key={cellNumber}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Paper
                        elevation={3}
                        sx={{
                          backgroundColor: cell.color,
                          padding: '8px',
                          flex: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          borderRadius: '8px 0 0 8px'
                        }}
                      >
                        <Typography variant="body2">
                          <FormattedMessage id="cell" defaultMessage="Cell" />{' '}
                          {cellNumber < 10 ? '0' : ''}
                          {cellNumber}
                        </Typography>
                        <Typography variant="body2">
                          {cell.value.toFixed(3)} V
                        </Typography>
                      </Paper>
                      <Box
                        sx={{
                          width: '10px',
                          height: '40px',
                          backgroundColor: '#666',
                          borderRadius: '0 4px 4px 0',
                          marginLeft: '2px'
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

const moduleRows = (module: PylontechModule) => [
  {
    key: 'moduleVoltage',
    label: (
      <FormattedMessage id="moduleVoltage" defaultMessage="Module Voltage" />
    ),
    value: format(module.ModuleVoltageV, 'V', 3)
  },
  {
    key: 'current',
    label: (
      <FormattedMessage
        id="chargeDischargeCurrent"
        defaultMessage="Charge/Discharge Current"
      />
    ),
    value: format(module.CurrentA, 'A', 2)
  },
  {
    key: 'residualCapacity',
    label: (
      <FormattedMessage
        id="residualCapacity"
        defaultMessage="Residual Capacity"
      />
    ),
    value: format(module.RemainCapacityAh, 'Ah', 2)
  },
  {
    key: 'fullCapacity',
    label: (
      <FormattedMessage id="batteryCapacity" defaultMessage="Battery Capacity" />
    ),
    value: format(module.FullCapacityAh, 'Ah', 2)
  },
  {
    key: 'cycleCount',
    label: (
      <FormattedMessage id="numberOfCycles" defaultMessage="Number of Cycles" />
    ),
    value: module.CycleCount ?? '—'
  },
  {
    key: 'minCellVoltage',
    label: (
      <FormattedMessage id="minCellVoltage" defaultMessage="Min Cell Voltage" />
    ),
    value: format(module.MinCellVoltageV, 'V', 3)
  },
  {
    key: 'maxCellVoltage',
    label: (
      <FormattedMessage id="maxCellVoltage" defaultMessage="Max Cell Voltage" />
    ),
    value: format(module.MaxCellVoltageV, 'V', 3)
  },
  {
    key: 'cellVoltageDiff',
    label: (
      <FormattedMessage
        id="cellVoltageDiff"
        defaultMessage="Cell Voltage Difference"
      />
    ),
    value: format(module.CellVoltageDiffV, 'V', 3)
  }
];

const ModuleBlock = ({ id, module }: ClusterModule) => (
  <Box sx={{ marginTop: '30px' }}>
    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '5px' }}>
      <FormattedMessage
        id="batteryModuleN"
        defaultMessage="Battery Module {n}"
        values={{ n: id }}
      />
    </Typography>

    {!hasBmsData(module) ? (
      <Card sx={{ ...cardStyle, padding: '20px' }}>
        <Typography variant="body1" fontWeight="bold">
          <FormattedMessage
            id="batteryDataNotAvailable"
            defaultMessage="Battery data not available"
          />
        </Typography>
        <Typography variant="body2" sx={{ marginTop: '5px' }}>
          <FormattedMessage
            id="bmsNoDataHint"
            defaultMessage="The battery management system is not reporting values for this module."
          />
        </Typography>
      </Card>
    ) : (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardTitle>
              <FormattedMessage
                id="batteryInformation"
                defaultMessage="Battery Information"
              />
            </CardTitle>
            <InfoTable rows={moduleRows(module)} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={cardStyle}>
            <CardTitle>
              <FormattedMessage
                id="cellVoltages"
                defaultMessage="Cell Voltages"
              />
            </CardTitle>
            <CellGrid module={module} />
          </Card>
        </Grid>
      </Grid>
    )}
  </Box>
);

const AlarmsCard = ({ battery }: { battery: InverterBattery }) => (
  <Card sx={{ ...cardStyle, marginTop: '30px', marginBottom: '30px' }}>
    <CardTitle>
      {battery.systemAlarms ? (
        <FormattedMessage
          id="systemAlarms"
          defaultMessage="System Alarms (whole installation)"
        />
      ) : (
        <FormattedMessage id="alarms" defaultMessage="Alarms" />
      )}
    </CardTitle>
    <TableContainer component={Paper} sx={{ width: '100%' }}>
      <Table size="small">
        <TableBody>
          {battery.alarms.length === 0 ? (
            <TableRow>
              <TableCell align="center">
                <FormattedMessage
                  id="noActiveAlarms"
                  defaultMessage="No active alarms"
                />
              </TableCell>
            </TableRow>
          ) : (
            battery.alarms.map((alarm, index) => (
              <TableRow key={`${index}-${alarm}`}>
                <TableCell sx={{ ...labelCellStyle, color: RED }}>
                  {battery.systemAlarms ? (
                    <FormattedMessage
                      id={`alarm_${alarm}`}
                      defaultMessage={splitCamelCase(alarm)}
                    />
                  ) : (
                    <FormattedMessage
                      id={alarmLabelKey(alarm)}
                      defaultMessage={alarm}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

function DetailedBatteryViewSodioHome(props: DetailedBatteryViewSodioHomeProps) {
  const navigate = useNavigate();
  const currentLocation = useLocation();

  // Router location, not window.location: the bare global happens to agree today, but
  // it is the un-prefixed browser path, so it would strip the wrong two segments the
  // moment this app is served under a router basename. Drops "detailed_view/{id}" to
  // land back on the cluster overview.
  const handleBatteryViewButton = () => {
    navigate(currentLocation.pathname.split('/').slice(0, -2).join('/'));
  };

  const modules = getClusterModules(props.batteryRecord, props.flatIdx);
  const battery = getInverterBattery(
    props.inverterRecord,
    props.invIdx,
    props.clIdx
  );

  return (
    <Box sx={{ width: '100%' }}>
      <IconButton
        aria-label="go back"
        sx={{
          marginTop: '30px',
          backgroundColor: 'grey',
          color: '#000000',
          '&:hover': { bgcolor: '#f7b34d' }
        }}
        onClick={handleBatteryViewButton}
      >
        <ArrowBackIcon />
      </IconButton>

      <Typography
        variant="h5"
        sx={{ textAlign: 'center', fontWeight: 'bold', marginTop: '10px' }}
      >
        {props.clusterLabel}
      </Typography>

      <ClusterSummary battery={battery} />

      {modules.length === 0 ? (
        <Card sx={{ ...cardStyle, marginTop: '30px', padding: '20px' }}>
          <Typography variant="body1" fontWeight="bold">
            <FormattedMessage
              id="batteryDataNotAvailable"
              defaultMessage="Battery data not available"
            />
          </Typography>
          <Typography variant="body2" sx={{ marginTop: '5px' }}>
            <FormattedMessage
              id="bmsNoDataHint"
              defaultMessage="The battery management system is not reporting values for this module."
            />
          </Typography>
        </Card>
      ) : (
        modules.map((entry) => (
          <ModuleBlock key={entry.id} id={entry.id} module={entry.module} />
        ))
      )}

      <AlarmsCard battery={battery} />
    </Box>
  );
}

export default DetailedBatteryViewSodioHome;
